import type { TreeItemContext } from '../types';

import { cn } from 'cn';
import { type MouseEvent, type PropsWithChildren } from 'react';

import { smoothScrollTo } from '@vestigas/utils';

import { buildAnchorLink } from '../lib/anchors';

import { Dot } from './Dot';
import { TreeAction } from './TreeAction';
import { TreeLevelConnector } from './TreeLevelConnector';

type P = PropsWithChildren<{
  /** Context for building the anchor link */
  context?: TreeItemContext;
  /** The ID of the currently focused/visible element */
  focusElementID?: string;
  /** Whether this is the first child in its parent group */
  isFirstChild?: boolean;
  /** Whether this is the last child in its parent group */
  isLastChild?: boolean;
  /** The nesting level (1 = root level) */
  level?: number;
  /** Replaces the default scroll-to-anchor behavior */
  onSelect?: (context: TreeItemContext) => void;
}> &
  ComponentStyling;

/**
 * Individual tree item with click-to-scroll functionality.
 * Displays active state when the corresponding form section is visible.
 * Includes visual connectors between tree levels.
 */
export function TreeItem({
  children,
  className,
  context = {},
  focusElementID,
  isFirstChild = false,
  isLastChild = false,
  level = 1,
  onSelect,
  style,
}: P) {
  let content = children;
  const anchorId = buildAnchorLink(Object.values(context));
  const href = anchorId ? `#${anchorId}` : undefined;

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();

    if (onSelect) {
      onSelect(context);
      return;
    }

    const element = globalThis.document.getElementById(anchorId);
    if (element) {
      smoothScrollTo(element, 0);
    }
  }

  const isActive = Boolean(
    focusElementID && anchorId && focusElementID === anchorId,
  );

  // Wrap string children in TreeAction
  if (typeof children === 'string') {
    content = (
      <TreeAction href={href} isActive={isActive} onClick={handleClick}>
        {content}
      </TreeAction>
    );
  } else if (Array.isArray(content)) {
    // If children is an array, first element is the title, rest are nested items
    const title = content.slice(0, 1)[0];

    content = (
      <>
        <TreeAction href={href} isActive={isActive} onClick={handleClick}>
          {title}
        </TreeAction>
        {content.slice(1)}
      </>
    );
  }

  return (
    <li
      className={cn(
        'flex w-full pl-2 whitespace-nowrap',
        {
          'font-medium text-tiga-text-default': isActive,
          'h-6 text-sm': level > 1,
          'mb-4 text-base': level === 1,
          'pt-4': level > 1,
          'text-tiga-text-lighter': !isActive,
        },
        className,
      )}
      style={style}
    >
      <div
        className={cn('relative w-full border-l-3 border-tiga-border-default', {
          'border-l-transparent': isLastChild,
        })}
      >
        <Dot isActive={isActive} />
        {level > 1 && isFirstChild ? (
          <TreeLevelConnector
            className="absolute -top-8.25 -left-7.5 w-7.5 stroke-tiga-border-default"
            style={{
              strokeWidth: 3,
              zIndex: -1,
            }}
          />
        ) : null}
        <div
          className={cn('relative flex flex-col pl-4', {
            '-translate-y-5': level === 1,
            '-translate-y-4.5': level > 1,
          })}
        >
          {content}
        </div>
      </div>
    </li>
  );
}
